"use client";

import type { ReactNode } from "react";
import { cn } from "../../lib";
import { FieldShell, type FieldLayoutHint } from "./field-shell";

export interface AssetRef {
	id: string;
	label?: string;
	kind?: string;
}

export interface AssetFieldProps {
	id: string;
	label: string;
	value: AssetRef[];
	onChange?: (value: AssetRef[]) => void;
	description?: string;
	error?: ReactNode;
	reserveErrorSpace?: boolean;
	name?: string;
	onBlur?: () => void;
	disabled?: boolean;
	layoutHint?: FieldLayoutHint;
	className?: string;
	/** Ingress actions (device, drag_drop, library, url, paste) — composed by SourcesCollector in P3. */
	actionsSlot?: ReactNode;
}

export function AssetField({
	id,
	label,
	value,
	description,
	error,
	reserveErrorSpace = false,
	name,
	onBlur,
	disabled = false,
	layoutHint,
	className,
	actionsSlot,
}: AssetFieldProps) {
	return (
		<FieldShell
			id={id}
			label={label}
			description={description}
			error={error}
			reserveErrorSpace={reserveErrorSpace}
			layoutHint={layoutHint}
			className={cn(disabled && "pointer-events-none opacity-50", className)}
		>
			{name ? (
				<input
					type="hidden"
					name={name}
					value={value.map((asset) => asset.id).join(",")}
					readOnly
				/>
			) : null}
			<div
				id={id}
				data-field-type="asset"
				className="rounded-md border border-dashed border-input bg-card p-4 text-sm"
				aria-invalid={Boolean(error) ? true : undefined}
				onBlur={onBlur}
			>
				{actionsSlot}
				{value.length === 0 ? (
					<p className="text-muted-foreground">No assets selected</p>
				) : (
					<ul className="flex flex-col gap-1">
						{value.map((asset) => (
							<li key={asset.id}>{asset.label ?? asset.id}</li>
						))}
					</ul>
				)}
			</div>
		</FieldShell>
	);
}
